import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { BookOpen, ChevronRight, Search, X } from 'lucide-react';

// Bundla todos os arquivos .md do manual em build time.
// Path: apps/web/src/modules/manual -> ../../../../../docs/manual-usuario/
//   apps/web/src/modules/manual -> apps/web/src/modules -> apps/web/src ->
//   apps/web -> apps -> sistema (root, onde mora docs/)
const arquivos = import.meta.glob('../../../../../docs/manual-usuario/*.md', {
  eager: true,
  query: '?raw',
  import: 'default',
});

interface Capitulo { slug: string; titulo: string; ordem: number; conteudo: string }

function construirCapitulos(): Capitulo[] {
  const lista: Capitulo[] = [];
  for (const [path, conteudo] of Object.entries(arquivos)) {
    const nomeArquivo = path.split('/').pop()!.replace('.md', '');
    if (nomeArquivo === 'index') continue;
    const slug = nomeArquivo;
    const matchTitulo = String(conteudo).match(/^#\s+(.+)$/m);
    const titulo = matchTitulo ? matchTitulo[1]!.trim() : nomeArquivo;
    const matchNum = nomeArquivo.match(/^(\d+)/);
    const ordem = matchNum ? parseInt(matchNum[1]!, 10) : 999;
    lista.push({ slug, titulo, ordem, conteudo: String(conteudo) });
  }
  return lista.sort((a, b) => a.ordem - b.ordem);
}

const CAPITULOS = construirCapitulos();

function semPrimeiroH1(md: string): string {
  return md.replace(/^#\s+.+\n+/, '');
}

export function ManualPage() {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const [busca, setBusca] = useState('');
  const buscaRef = useRef<HTMLInputElement>(null);
  const conteudoRef = useRef<HTMLDivElement>(null);

  const capituloAtual = useMemo(() => {
    if (slug) {
      const achado = CAPITULOS.find((c) => c.slug === slug);
      if (achado) return achado;
    }
    return CAPITULOS[0];
  }, [slug]);

  const capitulosFiltrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return CAPITULOS;
    return CAPITULOS.filter(
      (c) => c.titulo.toLowerCase().includes(q) || c.conteudo.toLowerCase().includes(q),
    );
  }, [busca]);

  function snippetBusca(conteudo: string, q: string): string | null {
    if (!q) return null;
    const lower = conteudo.toLowerCase();
    const idx = lower.indexOf(q.toLowerCase());
    if (idx < 0) return null;
    const start = Math.max(0, idx - 30);
    const end = Math.min(conteudo.length, idx + q.length + 50);
    let trecho = conteudo.substring(start, end).replace(/\n/g, ' ').replace(/#/g, '');
    if (start > 0) trecho = '...' + trecho;
    if (end < conteudo.length) trecho = trecho + '...';
    return trecho;
  }

  // Atalhos: Ctrl+K ou / focam busca; Esc limpa
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.ctrlKey && e.key === 'k') || (e.key === '/' && document.activeElement?.tagName !== 'INPUT')) {
        e.preventDefault();
        buscaRef.current?.focus();
      }
      if (e.key === 'Escape' && document.activeElement === buscaRef.current) {
        setBusca('');
        buscaRef.current?.blur();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Scroll pro topo ao trocar de capitulo
  useEffect(() => {
    if (conteudoRef.current) conteudoRef.current.scrollTop = 0;
  }, [capituloAtual?.slug]);

  function abrirCapitulo(s: string) {
    navigate(`/manual/${s}`);
  }

  if (!capituloAtual) {
    return <div className="p-8 text-gray-500">Nenhum capitulo encontrado.</div>;
  }

  return (
    <div className="manual-root flex h-[calc(100vh-3.5rem)]">
      {/* Sidebar */}
      <aside className="w-72 flex-shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col">
        <div className="p-3 border-b border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen size={18} className="text-blue-600" />
            <h2 className="font-bold text-gray-700">Manual do Usuario</h2>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              ref={buscaRef}
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar (Ctrl+K)"
              className="w-full pl-7 pr-7 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white"
            />
            {busca && (
              <button
                onClick={() => setBusca('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                title="Limpar busca"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-1">
          {capitulosFiltrados.length === 0 ? (
            <div className="px-4 py-6 text-sm text-gray-500 text-center">
              Nenhum capitulo encontrado para "{busca}"
            </div>
          ) : (
            capitulosFiltrados.map((c) => {
              const isAtual = c.slug === capituloAtual.slug;
              const snippet =
                busca && !c.titulo.toLowerCase().includes(busca.toLowerCase())
                  ? snippetBusca(c.conteudo, busca)
                  : null;
              return (
                <button
                  key={c.slug}
                  onClick={() => abrirCapitulo(c.slug)}
                  className={`w-full text-left px-3 py-2 text-sm transition border-l-4 ${
                    isAtual
                      ? 'bg-blue-50 border-blue-600 text-blue-900 font-medium'
                      : 'border-transparent text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-1">
                    <ChevronRight size={12} className={isAtual ? 'text-blue-600' : 'text-gray-400'} />
                    <span className="flex-1">{c.titulo}</span>
                  </div>
                  {snippet && (
                    <div className="text-xs text-gray-500 mt-1 ml-4 italic line-clamp-2">{snippet}</div>
                  )}
                </button>
              );
            })
          )}
        </nav>

        <div className="p-2 border-t border-gray-200 text-xs text-gray-500 text-center">
          {CAPITULOS.length} capitulo{CAPITULOS.length === 1 ? '' : 's'}
        </div>
      </aside>

      {/* Conteudo */}
      <div ref={conteudoRef} className="flex-1 overflow-y-auto bg-white">
        <div className="max-w-3xl mx-auto px-8 py-6">
          <div className="pagina-header">
            <h2 className="pagina-titulo">{capituloAtual.titulo}</h2>
          </div>
          <article className="manual-prose">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: (props) => <h2 className="text-xl font-bold text-gray-800 mt-8 mb-3" {...props} />,
                h2: (props) => <h2 className="text-xl font-bold text-gray-800 mt-8 mb-3" {...props} />,
                h3: (props) => <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-2" {...props} />,
                p: (props) => <p className="text-gray-700 leading-relaxed mb-4" {...props} />,
                ul: (props) => <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-700" {...props} />,
                ol: (props) => <ol className="list-decimal pl-6 mb-4 space-y-1 text-gray-700" {...props} />,
                li: (props) => <li className="leading-relaxed" {...props} />,
                strong: (props) => <strong className="font-semibold text-gray-900" {...props} />,
                code: ({ children, ...props }) => {
                  const isBlock = String(children ?? '').includes('\n');
                  return isBlock ? (
                    <code className="block bg-gray-100 text-gray-800 p-3 rounded text-sm font-mono overflow-x-auto" {...props}>
                      {children}
                    </code>
                  ) : (
                    <code className="bg-gray-100 text-pink-700 px-1.5 py-0.5 rounded text-sm font-mono" {...props}>
                      {children}
                    </code>
                  );
                },
                pre: (props) => <pre className="mb-4" {...props} />,
                a: ({ href, children, ...props }) => {
                  // Links internos (ex: 09-caixa.md) viram navegacao interna
                  const m = href?.match(/^([\w-]+)\.md$/);
                  if (m) {
                    const target = m[1]!;
                    return (
                      <a
                        href={`/manual/${target}`}
                        onClick={(e) => { e.preventDefault(); abrirCapitulo(target); }}
                        className="text-blue-600 hover:underline"
                        {...props}
                      >{children}</a>
                    );
                  }
                  return (
                    <a className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer" href={href} {...props}>
                      {children}
                    </a>
                  );
                },
                table: (props) => (
                  <div className="overflow-x-auto mb-4">
                    <table className="min-w-full border-collapse border border-gray-300" {...props} />
                  </div>
                ),
                thead: (props) => <thead className="bg-gray-100" {...props} />,
                th: (props) => <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-800" {...props} />,
                td: (props) => <td className="border border-gray-300 px-3 py-2 text-gray-700" {...props} />,
                blockquote: (props) => (
                  <blockquote className="border-l-4 border-blue-300 pl-4 italic text-gray-600 my-4" {...props} />
                ),
                hr: () => <hr className="border-gray-200 my-6" />,
              }}
            >
              {semPrimeiroH1(capituloAtual.conteudo)}
            </ReactMarkdown>
          </article>
        </div>
      </div>
    </div>
  );
}
